import { useId } from "react";
import { Controller, useFormContext, type FieldPath, type FieldValues } from "react-hook-form";

import { Label } from "../../atoms/label";
import { TextField } from "../../atoms/text-field";
import type { TextFieldProps } from "../../atoms/text-field";
import { cn, platformProps } from "../../lib";
import { Box } from "../../primitives";
import { FormFieldMessage } from "./form-field-message";
import type { FormFieldBaseProps } from "./types";

export type FormTextFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = FormFieldBaseProps<TFieldValues, TName> &
  Omit<TextFieldProps, "state" | "value" | "onChangeText" | "onBlur"> & {
    /**
     * Rewrites each keystroke before it reaches form state, so the stored value
     * and the displayed value stay identical (an input mask). Must be pure and
     * idempotent — it re-runs over its own output on every edit.
     */
    format?: (value: string) => string;
  };

export const FormTextField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  label,
  required = false,
  helperText,
  hideErrorMessage = false,
  errorMessage,
  containerClassName,
  testID,
  format,
  id: idProp,
  ...textFieldProps
}: FormTextFieldProps<TFieldValues, TName>) => {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const { control } = useFormContext<TFieldValues>();

  return (
    <Box
      {...platformProps({ slot: "form-text-field", testID })}
      className={cn("gap-spacing-xxs flex flex-col", containerClassName)}
    >
      {label && (
        <Label
          htmlFor={id}
          required={required}
          disabled={textFieldProps.disabled}
          size={textFieldProps.size}
        >
          {label}
        </Label>
      )}
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => {
          const hasError = !!fieldState.error;
          const displayError =
            fieldState.error && !hideErrorMessage
              ? (errorMessage ?? fieldState.error.message)
              : undefined;
          return (
            <>
              <TextField
                {...textFieldProps}
                ref={field.ref}
                // Native ignores the Label's htmlFor, so the input carries the label
                accessibilityLabel={textFieldProps.accessibilityLabel ?? label}
                id={id}
                testID={id}
                state={hasError ? "error" : "default"}
                value={field.value ?? ""}
                onChangeText={
                  format ? (next: string) => field.onChange(format(next)) : field.onChange
                }
                onBlur={field.onBlur}
              />
              <FormFieldMessage error={displayError} helperText={helperText} />
            </>
          );
        }}
      />
    </Box>
  );
};

FormTextField.displayName = "FormTextField";
